<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
    formatAuraDateLabel,
    formatAuraMetric,
    formatAuraStructuredTableValue,
} from '@/utils/auraPresentation';
import { getAuraChartColor } from '@/utils/auraChartPalette';

interface Props {
    labels: string[];
    data: unknown[];
    columns?: string[];
    tableRows?: Array<Record<string, unknown>>;
    unit?: string;
    selectedLabel?: string | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{ select: [label: string] }>();
const activeLabel = ref<string | null>(null);
const currentPage = ref(1);
const rowsPerPage = 25;

const isStructuredTable = computed(
    () => props.columns?.length && props.tableRows && props.tableRows.length > 0,
);
const structuredRows = computed(() => (isStructuredTable.value ? props.tableRows || [] : []));
const totalPages = computed(() =>
    Math.max(1, Math.ceil(structuredRows.value.length / rowsPerPage)),
);
const paginatedStructuredRows = computed(() => {
    const start = (currentPage.value - 1) * rowsPerPage;
    return structuredRows.value.slice(start, start + rowsPerPage);
});

watch(
    [structuredRows, totalPages],
    () => {
        if (currentPage.value > totalPages.value) currentPage.value = totalPages.value;
    },
    { immediate: true },
);

const rows = computed(() => {
    const numericValues = props.data.map((value) => (typeof value === 'number' ? value : 0));
    const total = numericValues.reduce((sum, value) => sum + Math.max(value, 0), 0);

    return props.labels.map((label, index) => {
        const rawValue = props.data[index];
        const value = typeof rawValue === 'number' || typeof rawValue === 'string' ? rawValue : '-';
        const numericValue = typeof rawValue === 'number' ? Math.max(rawValue, 0) : null;
        return {
            label,
            color: getAuraChartColor(index),
            value: formatAuraMetric(value, props.unit),
            percentage: numericValue !== null && total > 0 ? (numericValue / total) * 100 : null,
        };
    });
});

const selectAndActivate = (row: (typeof rows.value)[number]) => {
    activeLabel.value = row.label;
};

const selectRow = (row: (typeof rows.value)[number]) => {
    activeLabel.value = row.label;
    emit('select', row.label);
};

const formatColumnLabel = (column: string) => {
    const labels: Record<string, string> = {
        dispositivo: 'Dispositivo',
        fila: 'Fila',
        quantidade_de_usuarios: 'Quantidade de usuários',
        ramal: 'Ramal',
        usuario: 'Usuário',
    };
    return labels[column] || column.replaceAll('_', ' ');
};
</script>

<template>
    <div class="overflow-hidden rounded-xl border border-[#E5E5E5] bg-[#FBFCFC]">
        <div class="flex items-center justify-between border-b border-[#E5E5E5] px-4 py-3">
            <div>
                <p class="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#737373]">
                    Detalhamento
                </p>
                <p class="mt-0.5 text-sm font-semibold text-[#242B59]">
                    {{ isStructuredTable ? structuredRows.length : rows.length }}
                    {{ isStructuredTable ? 'registros' : 'categorias' }}
                </p>
            </div>
            <span
                class="rounded-full bg-[#48DDC3]/15 px-2.5 py-1 text-[10px] font-semibold text-[#168F7D]"
            >
                {{ isStructuredTable ? 'Lista detalhada' : 'Passe o mouse para explorar' }}
            </span>
        </div>
        <div class="overflow-x-auto">
            <table v-if="isStructuredTable" class="min-w-[640px] text-left text-xs text-[#242B59]">
                <caption class="sr-only">
                    Dados detalhados da consulta
                </caption>
                <thead class="bg-white text-[#626468]">
                    <tr>
                        <th
                            v-for="column in columns"
                            :key="column"
                            scope="col"
                            class="px-3 py-2 font-semibold"
                        >
                            {{ formatColumnLabel(column) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, index) in paginatedStructuredRows"
                        :key="`${currentPage}-${index}`"
                        class="border-t border-[#E5E5E5]"
                    >
                        <td
                            v-for="column in columns"
                            :key="column"
                            class="px-3 py-2 text-[#242B59]"
                        >
                            {{ formatAuraStructuredTableValue(column, row[column]) }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <table v-else class="min-w-[520px] text-left text-xs text-[#242B59]">
                <caption class="sr-only">
                    Dados detalhados do gráfico
                </caption>
                <thead class="bg-white text-[#626468]">
                    <tr>
                        <th scope="col" class="px-3 py-2 font-semibold">Categoria</th>
                        <th scope="col" class="px-3 py-2 text-right font-semibold">Valor</th>
                        <th scope="col" class="px-3 py-2 text-right font-semibold">Participação</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="row.label"
                        tabindex="0"
                        :class="[
                            'group border-t border-[#E5E5E5] outline-none transition-all duration-200 hover:bg-[#48DDC3]/10 focus:bg-[#48DDC3]/10 focus:ring-2 focus:ring-inset focus:ring-[#48DDC3]',
                            activeLabel === row.label || props.selectedLabel === row.label
                                ? 'bg-[#48DDC3]/10'
                                : '',
                        ]"
                        @mouseenter="selectAndActivate(row)"
                        @focus="selectAndActivate(row)"
                        @mouseleave="activeLabel = null"
                        @click="selectRow(row)"
                        @keydown.enter="selectRow(row)"
                    >
                        <th scope="row" class="max-w-[220px] truncate px-3 py-2 font-medium">
                            <span
                                class="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle"
                                :style="{ backgroundColor: row.color }"
                                aria-hidden="true"
                            />
                            {{ formatAuraDateLabel(row.label) }}
                        </th>
                        <td
                            class="px-3 py-2 text-right font-semibold"
                            :style="{ color: row.color }"
                        >
                            {{ row.value }}
                        </td>
                        <td class="w-1/3 px-3 py-2">
                            <div class="flex items-center gap-2">
                                <div
                                    class="h-1.5 min-w-16 flex-1 overflow-hidden rounded-full bg-[#E5E5E5]"
                                >
                                    <div
                                        class="h-full rounded-full transition-all duration-300"
                                        :style="{
                                            width: `${row.percentage ?? 0}%`,
                                            backgroundColor: row.color,
                                        }"
                                    />
                                </div>
                                <span
                                    class="w-12 text-right text-[11px] font-medium text-[#626468]"
                                >
                                    {{
                                        row.percentage === null
                                            ? '-'
                                            : `${row.percentage.toFixed(1)}%`
                                    }}
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div
            v-if="isStructuredTable && structuredRows.length > rowsPerPage"
            class="flex flex-col gap-2 border-t border-[#E5E5E5] px-3 py-2 text-xs text-[#626468] sm:flex-row sm:items-center sm:justify-between"
        >
            <span>
                Exibindo {{ (currentPage - 1) * rowsPerPage + 1 }} a
                {{ Math.min(currentPage * rowsPerPage, structuredRows.length) }} de
                {{ structuredRows.length }} registros
            </span>
            <div class="flex items-center gap-1">
                <button
                    type="button"
                    class="rounded border border-[#E5E5E5] px-2 py-1 font-medium disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="currentPage === 1"
                    aria-label="Página anterior"
                    @click="currentPage -= 1"
                >
                    Anterior
                </button>
                <span class="px-2">{{ currentPage }} / {{ totalPages }}</span>
                <button
                    type="button"
                    class="rounded border border-[#E5E5E5] px-2 py-1 font-medium disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="currentPage === totalPages"
                    aria-label="Próxima página"
                    @click="currentPage += 1"
                >
                    Próxima
                </button>
            </div>
        </div>
    </div>
</template>
